/**
 * materialBG — vanilla TS port of the React helper in
 * _design_ref_mobile_2026-05-24/render-pair.html:49-120.
 *
 * Produces the 2D tile fill for a square Material / Colorway / Leg swatch:
 *  - For textured finishes (boucle/linen/velvet/leather/oak/…) it composites the
 *    real fabric jpg under highlight/shadow gradients, tinted by the colorway hex
 *    via background-blend-mode.
 *  - For metal/matte finishes (matte/brass/chrome/steel/concrete) it draws a
 *    CSS-only gradient (no texture jpg exists for those).
 *
 * NOTE: this is the TILE thumbnail only — purely 2D. The 3D fabric is the GLB's
 * baked map tinted by engine.applyFabric(color). The two are deliberately
 * decoupled (see the build report risk note).
 *
 * Returns a Partial<CSSStyleDeclaration> to be Object.assign'd onto a chip's
 * .style. Textures are served from /mobile-textures/ (copied into public/).
 */

const TEXTURE_BASE = "/mobile-textures/";

/** finish kind → texture jpg (subset that has a real Polyhaven CC0 thumbnail). */
const MAT_TEXTURES: Record<string, string> = {
  boucle: "boucle.jpg",
  linen: "linen.jpg",
  velvet: "velvet.jpg",
  leather: "leather.jpg",
  cord: "velvet.jpg", // reuse — corduroy nap ≈ velvet
  tweed: "boucle.jpg", // reuse — tweed weave ≈ boucle
  mohair: "velvet.jpg", // reuse — mohair plush ≈ velvet
  oak: "oak.jpg",
  walnut: "oak.jpg", // reuse — wood grain, darker via tint
};

/** Shade a hex by percent (-100 darken .. +100 lighten). Port of shadeColor(). */
function shadeColor(hex: string, percent: number): string {
  const h = hex.replace("#", "");
  const num = parseInt(h, 16);
  let r = (num >> 16) & 0xff;
  let g = (num >> 8) & 0xff;
  let b = num & 0xff;
  const t = percent < 0 ? 0 : 255;
  const p = Math.abs(percent) / 100;
  r = Math.round((t - r) * p + r);
  g = Math.round((t - g) * p + g);
  b = Math.round((t - b) * p + b);
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}

/** Build the CSS fill for a swatch chip. `finish` is the material/leg finish kind. */
export function materialBG(finish: string, hex: string): Partial<CSSStyleDeclaration> {
  const L = (p: number) => shadeColor(hex, p);
  const tex = MAT_TEXTURES[finish];
  if (tex) {
    return {
      backgroundColor: hex,
      backgroundImage:
        `radial-gradient(circle at 32% 25%, rgba(255,255,255,0.30) 0%, transparent 40%), ` +
        `radial-gradient(ellipse at 50% 100%, rgba(0,0,0,0.30) 0%, transparent 50%), ` +
        `url('${TEXTURE_BASE}${tex}')`,
      backgroundSize: "cover, cover, cover",
      backgroundPosition: "center",
      backgroundBlendMode: "screen, multiply, multiply",
      backgroundRepeat: "no-repeat",
    };
  }
  switch (finish) {
    case "matte":
      return { background: `radial-gradient(circle at 30% 30%, ${L(12)} 0%, ${hex} 60%, ${L(-22)} 100%)` };
    case "brass":
      return {
        background:
          `radial-gradient(ellipse at 35% 18%, #fde080 0%, transparent 32%), ` +
          `radial-gradient(ellipse at 65% 78%, rgba(0,0,0,0.40) 0%, transparent 45%), ` +
          `linear-gradient(165deg, ${L(30)} 0%, ${hex} 38%, ${L(-40)} 80%, #2c1d08 100%)`,
      };
    case "chrome":
      return {
        background:
          `linear-gradient(135deg, #ffffff 0%, #d6d6d6 18%, #f8f8f8 32%, #909090 55%, #ededed 72%, #7e7e7e 88%, #d8d8d8 100%)`,
      };
    case "steel":
      return {
        background:
          `linear-gradient(135deg, #c0c0c0 0%, #707070 22%, #d6d6d6 50%, #5e5e5e 75%, #b0b0b0 100%)`,
      };
    case "concrete":
      return {
        background:
          `radial-gradient(circle at 30% 30%, ${L(15)} 0%, ${hex} 55%, ${L(-20)} 100%), ` +
          `radial-gradient(circle at 70% 80%, rgba(0,0,0,0.10) 0%, transparent 40%)`,
      };
    default:
      return { background: hex };
  }
}

/** Heuristic finish kind from an id when no explicit `finish` is set. */
export function finishFromId(id: string): string {
  const s = id.toLowerCase();
  for (const k of Object.keys(MAT_TEXTURES)) if (s.includes(k)) return k;
  for (const k of ["matte", "brass", "chrome", "steel", "concrete", "black", "dark", "noir"]) {
    if (s.includes(k)) return k === "black" || k === "dark" || k === "noir" ? "matte" : k;
  }
  return "matte";
}
